/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import theme from '../../../../../Shared/Themes/theme';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import { useLocation } from 'react-router-dom';
import Modal from '../../../../Jobs/Utilities/Shared/Components/Modal';
import { buttonStyles } from '../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import { HeadingText } from '../../../../Jobs/Components/JobDetails/OneTimeJobDetails.Style';
import MasterDataApiService from '../../../../../Shared/Services/MasterDataService';
import { IServiceCategoryItem, IServiceItem } from '../../../../Jobs/Models/AddEditOneTimeJob.Model';
import {
  getDefaultServiceValueForScheduleCreation,
  getStoredCustomerDetails,
} from '../../../../../Shared/Utilities/utils';
import TaskCreationFlow from './TaskCreation/TaskCreationFlow';
import JobDetails from './JobDetails';
import EditService from './EditService';
import AddServiceFlow from './AddServiceFlow';
import { CreateServiceDto, CreateTaskSubtaskMappingDto, ServiceTaskMapping } from '../../../../../Shared/Models';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import { ModifyServicesProvider, useModifyServicesContext } from './ModifyServicesContext';
import { useServiceOperations } from './hooks/useServiceOperations';
import { useTaskData } from './hooks/useTaskData';
import { DEFAULT_PAGE_NUMBER, ServiceCreationType, SUBTASK_TYPE } from '../../../../../Shared/Constants/App';

// eslint-disable-next-line sonarjs/cognitive-complexity
const ModifyServices: React.FC = () => {
  return (
    <ModifyServicesProvider>
      <ModifyServicesContent />
    </ModifyServicesProvider>
  );
};

const ModifyServicesContent: React.FC = () => {
  const { t } = useTranslation(['taskManagement', 'jobManagement', 'oneTimeJob', 'altTexts']);

  const {
    selectedServiceId,
    setSelectedServiceId,
    selectedTask,
    setSelectedTask,
    expandedTask,
    setExpandedTask,
    setOpenTaskId,
    selectedSubTask,
    setSelectedSubTask,
    subTask,
    setSubTask,
    searchString,
    setSearchString,
    setSearchSubTaskString,
    currentPage,
    setCurrentPage,
    setIsLoading,
    isTasksLoading,
    setIsTasksLoading,
    isServicesLoading,
    setIsServicesLoading,
    showSnackbar,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    closeSnackbar,
  } = useModifyServicesContext();

  const location = useLocation();
  const selectedJobs = location.state?.selectedRows || [];
  const { userId = '' } = getStoredCustomerDetails() || {};

  const serviceOperations = useServiceOperations(userId, showSnackbar);
  const taskData = useTaskData(userId, showSnackbar);

  const [servicesData, setServicesData] = useState<any>(null);
  const [isEditService, setIsEditService] = useState(false);
  const [isAddService, setIsAddService] = useState(false);

  const [addNewTask, setAddNewTask] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [nextClick, setNextClick] = useState(false);
  const [taskName, setTaskName] = useState<string>('');
  const [taskDescription, setTaskDescription] = useState<string>('');
  const [isSubTasksOpen, setSubTasksOpen] = useState(false);

  const [service, setService] = useState<IServiceItem[]>(getDefaultServiceValueForScheduleCreation());
  const [serviceTasks, setServiceTasks] = useState<any>({});
  const [taskSubTasks, setTaskSubTasks] = useState<any[]>([]);
  const [createSubTask, setCreateSubTask] = useState(false);
  const [subTaskName, setSubTaskName] = useState<string>('');
  const [subTaskDescription, setSubTaskDescription] = useState<string>('');
  const [selectedSubTaskType, setSelectedSubTaskType] = useState<string>('');
  const [createNewTask, setCreateNewTask] = useState(false);

  const [serviceTaskMapping, setServiceTaskMapping] = useState<ServiceTaskMapping>();

  const [isServiceTaskLoading, setIsServiceTaskLoading] = useState(false);

  const [serviceName, setServiceName] = useState<string>('');
  const [serviceDescription, setServiceDescription] = useState<string>('');
  const [createService, setCreateService] = useState(false);

  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState(false);

  const [serviceConfirmed, setServiceConfirmed] = useState(false);
  const [taskConfirmed, setTaskConfirmed] = useState(false);
  const [subTaskConfirmed, setSubTaskConfirmed] = useState(false);

  const [isTasksOpen, setIsTasksOpen] = useState(false);

  const [selectedTaskType, setSelectedTaskType] = useState<string>('');

  const [createdService, setCreatedService] = useState<any>();

  const [createdTask, setCreatedTask] = useState<any>();

  const [createdSubTask, setCreatedSubTask] = useState<any>();

  const [selectedTaskObj, setSelectedTaskObj] = useState<any>(null);

  const [subTaskCreationType, setSubTaskCreationType] = useState<string>('');

  const [addServiceToSchedule, setAddServiceToSchedule] = useState<boolean>(false);
  const [serviceCreationType, setServiceCreationType] = useState<string>('');

  const { radioButton, radioButtonText } = buttonStyles;

  const handleToggle = (taskId: string) => {
    setExpandedTask((prev) => (prev === taskId ? null : taskId));
  };

  const handleOptionChange = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSelectedOption(event.target.value);
  };

  const handleSubTaskTypeChange = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSelectedSubTaskType(event.target.value);
    setSubTaskConfirmed(false);
    setSelectedSubTask('');
    setSubTaskName('');
    setSubTaskDescription('');
    setSearchSubTaskString('');
    setSearchString('');
  };

  const handleServiceCreationType = (event: { target: { value: React.SetStateAction<string> } }) => {
    setServiceCreationType(event.target.value);
  };

  const handleSubTaskCreationType = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSubTaskCreationType(event.target.value);
  };
  const handleTaskTypeChange = (event: { target: { value: React.SetStateAction<string> } }) => {
    setSelectedTaskType(event.target.value);
    setTaskConfirmed(false);
    setSelectedTask('');
    setTaskName('');
    setTaskDescription('');
    setSearchString('');
    setSearchSubTaskString('');
  };
  const fetchScheduleMasterServices = async () => {
    if (selectedJobs.length === 0) return;

    setIsLoading(true);
    try {
      const woScheduleMasterId = selectedJobs?.[0]?.woScheduleMasterId;
      const response = await AllJobsApiService.getMasterServices(woScheduleMasterId);
      setServicesData(response?.data);
      return response.data;
    } catch (error) {
      console.error(t('jobManagement:errorFetchingServices'), error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getServiceCategories = async () => {
    try {
      setIsServicesLoading(true);
      const customerId = selectedJobs?.[0]?.customerId;
      const allServiceCategoryV2Response = await MasterDataApiService.getAllServiceCategoryV3(customerId);
      if (allServiceCategoryV2Response?.data) {
        taskData.setAvailableTasksData(allServiceCategoryV2Response?.data);
        const modifiedCategories = allServiceCategoryV2Response?.data?.map((categoryItem: any) => {
          return {
            categoryId: categoryItem?.serviceCategoryId,
            categoryName: categoryItem?.serviceCategoryName,
            unit: categoryItem?.unit,
          };
        });
        const uniqueCategories: IServiceCategoryItem[] = [
          ...new Map(modifiedCategories?.map((item: any) => [item?.categoryId, item])).values(),
        ] as IServiceCategoryItem[];
        uniqueCategories.sort((a: any, b: any) => a?.categoryName?.localeCompare(b?.categoryName));
        fetchTaskDetails(allServiceCategoryV2Response);
      }
      setIsServicesLoading(false);
    } catch (error) {
      console.error(t('jobManagement:errorFetchingServiceCategories'), error);
    }
  };

  const fetchTaskDetails = (allServiceCategoryV2Response: any) => {
    const uniqueTaskData = allServiceCategoryV2Response?.data?.reduce((acc: any, task: any) => {
      const { serviceCategoryId } = task;
      if (!acc[serviceCategoryId]) {
        acc[serviceCategoryId] = [];
      }
      acc[serviceCategoryId]?.push(task);
      return acc;
    }, {});
    setServiceTasks?.(uniqueTaskData);
  };

  const handleTaskChanges = (event: { target: { value: any } }) => {
    const value = event.target.value;
    setSelectedTask(value);
    taskData.handleTaskSelection(value, setTaskSubTasks, setTaskConfirmed);
  };

  const handleSubTaskChanges = (event: { target: { value: any } }) => {
    const value = event.target.value;
    setSubTask(value);
    taskData.handleSubTaskSelection(value, setSubTaskConfirmed);
  };

  const handlePaginationChange = (event: any, value: number) => {
    setCurrentPage(value);
    taskData.fetchAllTasks(value, searchString || '');
  };

  const fetchAllTasks = async (pageNumber: number, searchValue?: string) => {
    setIsTasksLoading(true);
    await taskData.fetchAllTasks(pageNumber, searchValue || '');
    setIsTasksLoading(false);
  };

  const handleCreateTask = (taskName: string, taskDescription: string) => {
    return serviceOperations.handleCreateTask(taskName, taskDescription);
  };

  const handleServiceTaskMapping = async () => {
    setIsServiceTaskLoading(true);
    if (!serviceTaskMapping) {
      console.error('ServiceTaskMapping is undefined');
      setIsServiceTaskLoading(false);
      return;
    }

    try {
      await serviceOperations.handleServiceTaskMapping(serviceTaskMapping);
      await fetchScheduleMasterServices();
    } finally {
      setIsServiceTaskLoading(false);
    }
  };

  const handleCreateService = (createServicePayload: CreateServiceDto[]) => {
    return serviceOperations.handleCreateService(createServicePayload);
  };

  const handleTaskSubTaskMapping = (taskSubTaskPayload: CreateTaskSubtaskMappingDto) => {
    return serviceOperations.handleTaskSubTaskMapping(taskSubTaskPayload);
  };

  const handleConfirmService = async () => {
    try {
      const createServicePayload: CreateServiceDto[] = [
        {
          serviceCategoryName: serviceName,
          categoryDescription: serviceDescription,
          mdServiceType: '',
          orgId: selectedJobs?.[0]?.customerId,
          taskMasterId: [],
        },
      ];

      const newServiceResponse = await handleCreateService(createServicePayload);

      if (newServiceResponse) {
        const newServiceItem = {
          mdServiceId: newServiceResponse.mdServiceId,
          mdServiceName: newServiceResponse.mdServiceName,
          mdServiceDescription: newServiceResponse.mdServiceDescription,
        };
        setCreatedService(newServiceItem);
      } else {
        console.error('Failed to create new service');
        return;
      }

      setServiceConfirmed(true);
      setCreateNewTask(true);
    } catch (error) {
      console.error('Error confirming service', error);
    }
  };

  const handleConfirmTask = async () => {
    try {
      const newTaskResponse = await handleCreateTask(taskName, taskDescription);
      if (newTaskResponse) {
        const newTaskItem = {
          mdTaskId: newTaskResponse.mdTaskId,
          mdTaskName: newTaskResponse.mdTaskName,
          mdTaskDescription: newTaskResponse.mdTaskDescription,
        };
        setCreatedTask(newTaskItem);
      } else {
        console.error('Failed to create new task');
        return;
      }

      setTaskConfirmed(true);
    } catch (error) {
      console.error('Error confirming task', error);
    }
  };

  const handleConfirmSubTask = async () => {
    try {
      const newSubTaskResponse = await handleCreateTask(subTaskName, subTaskDescription);
      if (newSubTaskResponse) {
        const newSubTaskItem = {
          mdTaskId: newSubTaskResponse?.mdTaskId,
          mdTaskName: newSubTaskResponse?.mdTaskName,
          mdTaskDescription: newSubTaskResponse?.mdTaskDescription,
        };
        setCreatedSubTask(newSubTaskItem);
      } else {
        console.error('Failed to create new subtask');
        return;
      }
      setSubTaskConfirmed(true);
    } catch (error) {
      console.error('Error confirming subtask', error);
    }
  };

  const handleCancelService = () => {
    setService(getDefaultServiceValueForScheduleCreation());
    setServiceName('');
    setServiceDescription('');
    setCreateService(false);

    setServiceConfirmed(false);

    handleCancelTask();
  };

  const handleCancelTask = () => {
    setSelectedTask('');
    setTaskName('');
    setTaskDescription('');
    setCreateNewTask(false);

    setTaskConfirmed(false);

    handleCancelSubTask();
  };

  const handleCancelSubTask = () => {
    setSelectedSubTask('');
    setSubTaskName('');
    setSubTaskDescription('');
    setCreateSubTask(false);
    setSelectedSubTaskType('');

    setSubTaskConfirmed(false);
  };

  useEffect(() => {
    fetchScheduleMasterServices();
  }, [selectedJobs]);

  useEffect(() => {
    if (isEditService || isAddService) {
      getServiceCategories();
    }
  }, [isEditService, isAddService]);

  useEffect(() => {
    if (isEditService || isAddService) {
      fetchAllTasks(currentPage);
    }
  }, [selectedOption, nextClick, isEditService, isAddService]);

  const renderAddNewTaskHeaderContent = () => {
    return (
      <HeadingText sx={{ fontSize: theme.typography.h2?.fontSize }}>
        {selectedOption === 'create-new-task' && nextClick
          ? t('taskManagement:CreateNewTask')
          : t('taskManagement:AddTask')}
      </HeadingText>
    );
  };

  const renderAddNewTaskBodyContent = () => {
    return (
      <TaskCreationFlow
        nextClick={nextClick}
        selectedOption={selectedOption}
        taskName={taskName}
        taskDescription={taskDescription}
        createSubTask={createSubTask}
        selectedSubTask={selectedSubTask}
        selectedTask={selectedTask}
        taskOptions={taskData.taskOptions}
        taskSubTasks={taskSubTasks}
        isTasksLoading={isTasksLoading}
        totalCount={taskData.totalCount}
        currentPage={currentPage}
        searchString={searchString}
        subTaskName={subTaskName}
        subTaskDescription={subTaskDescription}
        selectedSubTaskType={selectedSubTaskType}
        isSubTasksOpen={isSubTasksOpen}
        setNextClick={setNextClick}
        setAddNewTask={setAddNewTask}
        setTaskName={setTaskName}
        setTaskDescription={setTaskDescription}
        setCreateSubTask={setCreateSubTask}
        setSelectedSubTask={setSelectedSubTask}
        setSubTaskName={setSubTaskName}
        setSubTaskDescription={setSubTaskDescription}
        setSearchString={setSearchString}
        setSubTasksOpen={setSubTasksOpen}
        handleTaskChanges={handleTaskChanges}
        handlePaginationChange={handlePaginationChange}
        handleSubTaskTypeChange={handleSubTaskTypeChange}
        handleOptionChange={handleOptionChange}
        handleCreateTask={handleCreateTask}
        mainTasksObj={taskData.mainTasksObj}
        setServiceTaskMapping={setServiceTaskMapping}
        handleServiceTaskMapping={handleServiceTaskMapping}
        isServiceTaskLoading={isServiceTaskLoading}
        setIsEditService={setIsEditService}
        handleTaskSubTaskMapping={handleTaskSubTaskMapping}
        setSelectedTask={setSelectedTask}
        handleSubTaskChanges={handleSubTaskChanges}
        subTask={subTask}
        subTasksObj={taskData.subTasksObj}
        setSubTask={setSubTask}
        setSelectedOption={setSelectedOption}
        setSelectedServiceId={setSelectedServiceId}
      />
    );
  };

  const selectedService =
    servicesData?.find((s: any) => s.mdServiceId === selectedServiceId) || servicesData?.[0] || {};
  const currentTaskIds = selectedService?.tasks?.map((t: any) => t.mdTaskId) || [];

  const filteredAvailableTasks = taskData.availableTasksData?.filter(
    (task: any) => !currentTaskIds.includes(task.taskId)
  );

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleConfirmButton = async () => {
    try {
      setIsConfirmButtonLoading(true);

      let serviceId = '';
      let serviceNameValue = '';
      let serviceDescriptionValue = '';

      if (service?.[0]?.mdServiceId) {
        serviceId = service[0].mdServiceId;
        serviceNameValue = service[0].name || '';
        serviceDescriptionValue = '';
      } else if (createService && serviceName && serviceDescription) {
        serviceId = createdService.mdServiceId;
        serviceNameValue = createdService.mdServiceName;
        serviceDescriptionValue = createdService.mdServiceDescription;
      }

      let taskId = '';
      let taskNameValue = '';
      let taskDescriptionValue = '';

      if (selectedTaskObj) {
        taskId = selectedTaskObj?.taskId;
        taskNameValue = selectedTaskObj?.taskName || '';
        taskDescriptionValue = selectedTaskObj?.taskDescription || '';
      } else if (selectedTask) {
        taskId = selectedTask;
        taskNameValue = taskData.mainTasksObj?.taskName || '';
        taskDescriptionValue = taskData.mainTasksObj?.taskDescription || '';
      } else if (createNewTask && taskName && taskDescription) {
        taskId = createdTask.mdTaskId;
        taskNameValue = createdTask.mdTaskName || '';
        taskDescriptionValue = createdTask.mdTaskDescription || '';
      }

      let subTaskId = '';
      let subTaskNameValue = '';
      let subTaskDescriptionValue = '';

      if (selectedSubTask && !createSubTask) {
        subTaskId = selectedSubTask;
        subTaskNameValue = taskData.taskOptions.find((t) => t.value === selectedSubTask)?.label || '';
        subTaskDescriptionValue = taskData.subTasksObj?.taskDescription || '';
      } else if (createSubTask) {
        if (selectedSubTaskType === 'create-sub-task' && subTaskName && subTaskDescription) {
          subTaskId = createdSubTask.mdTaskId;
          subTaskNameValue = createdSubTask.mdTaskName || '';
          subTaskDescriptionValue = createdSubTask.mdTaskDescription || '';
        } else if (selectedSubTaskType === 'select-from-system' && taskData.subTasksObj) {
          subTaskId = taskData.subTasksObj?.mdTaskId;
          subTaskNameValue = taskData.subTasksObj?.taskName || '';
          subTaskDescriptionValue = taskData.subTasksObj?.taskDescription || '';
        }
      }

      const taskPayload: ServiceTaskMapping['tasks'] = [
        {
          mdTaskId: taskId,
          mdTaskName: taskNameValue,
          mdTaskDescription: taskDescriptionValue,
          isProofOfCompletionRequired: false,
          isInspectionRequired: false,
          inspectionEntity: '',
          subTasks: [
            {
              mdTaskId: subTaskId || '',
              mdTaskName: subTaskNameValue || '',
              mdTaskDescription: subTaskDescriptionValue || '',
            },
          ],
        },
      ];

      if (subTaskCreationType === SUBTASK_TYPE.PERMANENT) {
        const taskSubTaskPayload: CreateTaskSubtaskMappingDto = {
          tasks: [
            {
              mdTaskId: taskId || '',
              mdTaskName: taskNameValue || '',
              mdTaskDescription: taskDescriptionValue || '',
              subTasks: [
                {
                  mdTaskId: subTaskId || '',
                  mdTaskName: subTaskNameValue || '',
                  mdTaskDescription: subTaskDescriptionValue || '',
                },
              ],
            },
          ],
        };
        handleTaskSubTaskMapping(taskSubTaskPayload);
      }

      const payload: ServiceTaskMapping = {
        type: serviceCreationType,
        woScheduleMasterId: selectedJobs?.[0]?.woScheduleMasterId,
        mdServiceId: serviceId,
        mdServiceName: serviceNameValue,
        mdServiceDescription: serviceDescriptionValue,
        tasks: taskPayload,
        serviceMeasure: Number(service?.[0]?.quantity) || 0,
        mdServiceUnits: service?.[0]?.unit || '',
        fromDate: '',
        endDate: '',
        customerId: selectedJobs?.[0]?.customerId || '',
      };

      const response = await MasterDataApiService.serviceTaskMapping(userId, payload);
      if (response && response?.data) {
        if (response?.status === 200 || response?.status === 201 || response?.status === 204) {
          showSnackbar('success', 'Service task mapping created successfully');
        } else {
          showSnackbar('error', 'Something went wrong, please try again.');
        }
      } else {
        showSnackbar('error', `Error occurred while creating service task mapping. Please try again`);
      }

      resetAllStates();
      setAddServiceToSchedule(false);
      setIsAddService(false);
      await fetchScheduleMasterServices();
      setIsConfirmButtonLoading(false);
    } catch (error) {
      console.error('Error creating service task mapping', error);
      setIsConfirmButtonLoading(false);
    }
  };

  const renderAddServiceContent = () => {
    return (
      <>
        <FormControl component="fieldset">
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.h3?.fontSize,
              fontWeight: theme.typography.fontWeightBold,
            }}>
            {
              <span>
                {t('taskManagement:DoYouWantToAddTaskPermanentlyOrSchedule')}
                <span className="mandatory-field"> *</span>
              </span>
            }
          </Typography>
          <RadioGroup value={serviceCreationType} onChange={handleServiceCreationType}>
            <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
              <FormControlLabel
                value={ServiceCreationType.SCHEDULE_LEVEL}
                control={
                  <Radio
                    sx={{
                      ...radioButton,
                    }}
                  />
                }
                label={t('taskManagement:ScheduleLevel')}
                sx={{
                  ...radioButtonText,
                }}
              />
            </Box>
            <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
              <FormControlLabel
                value={ServiceCreationType.CUSTOMER_LEVEL}
                control={
                  <Radio
                    sx={{
                      ...radioButton,
                    }}
                  />
                }
                label={t('taskManagement:CustomerLevel')}
                sx={{
                  ...radioButtonText,
                }}
              />
            </Box>
          </RadioGroup>
        </FormControl>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
          <Button
            type={ButtonType.Secondary}
            label={t('taskManagement:Cancel')}
            onClick={() => {
              setAddServiceToSchedule(false);
            }}
          />
          <Button
            type={ButtonType.Primary}
            label={t('taskManagement:Confirm')}
            onClick={() => handleConfirmButton()}
            isLoading={isConfirmButtonLoading}
          />
        </Box>
      </>
    );
  };

  const resetAllStates = () => {
    setService(getDefaultServiceValueForScheduleCreation());
    setServiceName('');
    setServiceDescription('');
    setCreateService(false);
    setServiceConfirmed(false);

    setSelectedTaskObj(null);
    setCreateNewTask(false);
    setSelectedTaskType('');
    setSelectedTask('');
    setSearchString('');
    setTaskName('');
    setTaskDescription('');
    setTaskConfirmed(false);

    setSelectedSubTask('');
    setCreateSubTask(false);
    setSelectedSubTaskType('');
    setSearchSubTaskString('');
    setSubTaskConfirmed(false);
    setSubTaskName('');
    setSubTaskDescription('');
    setSubTaskCreationType('');
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const resetTasksAndSubTasks = () => {
    setSelectedTaskObj(null);
    setCreateNewTask(false);
    setSelectedTaskType('');
    setSelectedTask('');
    setSearchString('');
    setTaskName('');
    setTaskDescription('');
    setTaskConfirmed(false);
    setCreatedTask(null);

    setSelectedSubTask('');
    setCreateSubTask(false);
    setSelectedSubTaskType('');
    setSearchSubTaskString('');
    setSubTaskConfirmed(false);
    setSubTaskName('');
    setSubTaskDescription('');
    setSubTaskCreationType('');
    setCreatedSubTask(null);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  const resetSubTasks = () => {
    setSelectedSubTask('');
    setCreateSubTask(false);
    setSelectedSubTaskType('');
    setSearchSubTaskString('');
    setSubTaskConfirmed(false);
    setSubTaskName('');
    setSubTaskDescription('');
    setSubTaskCreationType('');
    setCreatedSubTask(null);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
  };

  return (
    <>
      <PageTitle title={t('jobManagement:modifyServices')} />
      <CustomerContainer>
        <JobDetails
          selectedJobs={selectedJobs}
          servicesData={servicesData}
          setIsEditService={setIsEditService}
          setIsAddService={setIsAddService}
          setServiceTaskMapping={setServiceTaskMapping}
          isEditService={isEditService}
          isAddService={isAddService}
          isServiceTaskLoading={isServiceTaskLoading}
        />

        {isEditService && (
          <EditService
            servicesData={servicesData}
            expandedTask={expandedTask}
            filteredAvailableTasks={filteredAvailableTasks}
            subTask={subTask}
            taskOptions={taskData.taskOptions}
            isTasksLoading={isTasksLoading}
            totalCount={taskData.totalCount}
            currentPage={currentPage}
            searchString={searchString}
            setOpenTaskId={setOpenTaskId}
            setIsEditService={setIsEditService}
            setAddNewTask={setAddNewTask}
            setSearchString={setSearchString}
            setSubTask={setSubTask}
            handleToggle={handleToggle}
            handleSubTaskChanges={handleSubTaskChanges}
            handlePaginationChange={handlePaginationChange}
            setServiceTaskMapping={setServiceTaskMapping}
            handleCreateTask={handleCreateTask}
            handleServiceTaskMapping={handleServiceTaskMapping}
            handleTaskSubTaskMapping={handleTaskSubTaskMapping}
            isServiceTaskLoading={isServiceTaskLoading}
            subTasksObj={taskData.subTasksObj}
            isServicesLoading={isServicesLoading}
            isTaskSubTaskMapLoading={serviceOperations.isTaskSubTaskMapLoading}
            isTaskCreationLoading={serviceOperations.isTaskCreationLoading}
            fetchScheduleMasterServices={fetchScheduleMasterServices}
            selectedServiceId={selectedServiceId}
            setSelectedServiceId={setSelectedServiceId}
          />
        )}
        {isAddService && (
          <AddServiceFlow
            service={service}
            setService={setService}
            serviceTasks={serviceTasks}
            setServiceTasks={setServiceTasks}
            selectedJobs={selectedJobs}
            servicesData={servicesData}
            serviceName={serviceName}
            setServiceName={setServiceName}
            serviceDescription={serviceDescription}
            setServiceDescription={setServiceDescription}
            createService={createService}
            setCreateService={setCreateService}
            serviceConfirmed={serviceConfirmed}
            setServiceConfirmed={setServiceConfirmed}
            createdService={createdService}
            isCreateServiceLoading={serviceOperations.isCreateServiceLoading}
            handleConfirmService={handleConfirmService}
            handleCancelService={handleCancelService}
            createNewTask={createNewTask}
            setCreateNewTask={setCreateNewTask}
            isTasksOpen={isTasksOpen}
            setIsTasksOpen={setIsTasksOpen}
            taskConfirmed={taskConfirmed}
            setTaskConfirmed={setTaskConfirmed}
            createdTask={createdTask}
            selectedTaskType={selectedTaskType}
            handleTaskTypeChange={handleTaskTypeChange}
            taskOptions={taskData.taskOptions}
            handleTaskChanges={handleTaskChanges}
            totalCount={taskData.totalCount}
            handlePaginationChange={handlePaginationChange}
            taskName={taskName}
            setTaskName={setTaskName}
            taskDescription={taskDescription}
            setTaskDescription={setTaskDescription}
            isCreateTaskLoading={serviceOperations.isTaskCreationLoading}
            handleCancelTask={handleCancelTask}
            handleConfirmTask={handleConfirmTask}
            isSubTasksOpen={isSubTasksOpen}
            setSubTasksOpen={setSubTasksOpen}
            taskSubTasks={taskSubTasks}
            setSubTaskConfirmed={setSubTaskConfirmed}
            createSubTask={createSubTask}
            setCreateSubTask={setCreateSubTask}
            selectedSubTaskType={selectedSubTaskType}
            setSelectedSubTaskType={setSelectedSubTaskType}
            handleSubTaskTypeChange={handleSubTaskTypeChange}
            createdSubTask={createdSubTask}
            handleSubTaskChanges={handleSubTaskChanges}
            subTaskCreationType={subTaskCreationType}
            handleSubTaskCreationType={handleSubTaskCreationType}
            subTaskName={subTaskName}
            setSubTaskName={setSubTaskName}
            subTaskDescription={subTaskDescription}
            setSubTaskDescription={setSubTaskDescription}
            isCreateSubTaskLoading={serviceOperations.isTaskCreationLoading}
            handleConfirmSubTask={handleConfirmSubTask}
            subTaskConfirmed={subTaskConfirmed}
            setIsAddService={setIsAddService}
            resetAllStates={resetAllStates}
            setAddServiceToSchedule={setAddServiceToSchedule}
            setSelectedTaskObj={setSelectedTaskObj}
            resetTasksAndSubTasks={resetTasksAndSubTasks}
            resetSubTasks={resetSubTasks}
          />
        )}
        <Modal
          open={addNewTask}
          onClose={() => {
            setAddNewTask(false);
            setNextClick(false);
            setSelectedOption('');
            setSelectedTask('');
            setSubTaskName('');
            setSubTaskDescription('');
            setSelectedSubTaskType('');
            setCreateSubTask(false);
            setSearchString('');
            setTaskName('');
            setTaskDescription('');
            setSelectedSubTask('');
          }}
          renderHeader={renderAddNewTaskHeaderContent()}
          renderBody={renderAddNewTaskBodyContent()}
          styles={{
            '& .MuiPaper-root': {
              width: nextClick ? '52rem' : '32rem',
            },
          }}
        />
        <Modal
          open={addServiceToSchedule}
          onClose={() => {
            setAddServiceToSchedule(false);
          }}
          renderHeader={''}
          renderBody={renderAddServiceContent()}
          styles={{
            '& .MuiPaper-root': {
              width: '52rem',
            },
          }}
          isRemoveDivider={true}
        />
      </CustomerContainer>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={closeSnackbar}
      />
    </>
  );
};

export default ModifyServices;
